import { CreditApplicationPlaidReport } from '@dill/dill-shared';
import moment from 'moment';

// Last 12 months ending this month, formatted to match the chart x-axis
const getLast12Months = () =>
  Array.from({ length: 12 }, (_, i) =>
    moment()
      .subtract(11 - i, 'months')
      .format('MMM YYYY')
  );

// Pattern A: low → mid-year spike → settle (mirrors typical account activity)
// Pattern B: different cadence for multi-account graphs
const BALANCE_PATTERNS = [
  [1920, 1540, 2680, 2350, 6650, 2720, 2080, 1980, 3280, 2650, 2180, 2220],
  [3100, 4750, 3580, 5240, 2820, 6080, 4350, 7150, 5620, 3940, 4720, 6310],
];

const buildTransactions = (
  baseDate: string,
  amounts: number[],
  category: string,
  namePrefix: string
) =>
  amounts.map((amount, i) => ({
    name: `${namePrefix} #${i + 1}`,
    description: `${category} transaction`,
    date: baseDate,
    amount,
    detailedCategory: category,
  }));

// Pick specific months within the 12-month window by offset from 12 months ago
const monthDate = (offsetFromStart: number, day: number) =>
  moment()
    .subtract(11 - offsetFromStart, 'months')
    .date(day)
    .format('YYYY-MM-DD');

const getFeeMonths = () => [
  { date: monthDate(2, 14), amounts: [34, 34], total: 68, count: 2 },
  { date: monthDate(4, 9), amounts: [34], total: 34, count: 1 },
  { date: monthDate(7, 21), amounts: [34, 34, 34], total: 102, count: 3 },
  { date: monthDate(10, 8), amounts: [34], total: 34, count: 1 },
];

const getLoanMonths = () => [
  { date: monthDate(1, 1), amounts: [1200, 800], total: 2000, count: 2 },
  { date: monthDate(3, 1), amounts: [1200, 800, 500], total: 2500, count: 3 },
  { date: monthDate(6, 1), amounts: [1200], total: 1200, count: 1 },
  { date: monthDate(8, 1), amounts: [1200, 800], total: 2000, count: 2 },
  { date: monthDate(11, 1), amounts: [1200, 800, 600], total: 2600, count: 3 },
];

export const getSamplePlaidMonthlyReport = (
  bankNames: string[]
): CreditApplicationPlaidReport[] => {
  const names = bankNames.length > 0 ? bankNames : ['Chase Bank', 'Wells Fargo'];
  const months = getLast12Months();
  const feeMonths = getFeeMonths();
  const loanMonths = getLoanMonths();

  return names.map((name, idx) => {
    const balancePattern = BALANCE_PATTERNS[idx % BALANCE_PATTERNS.length];

    return {
      id: `sample-plaid-${idx}`,
      name,
      historicalBalance: months.map((month, i) => ({
        month,
        amount: balancePattern[i],
      })),
      feesTransactionsSummary: feeMonths.map((m) => ({
        date: m.date,
        amount: m.total,
        numberOfTransactions: m.count,
        transactions: buildTransactions(m.date, m.amounts, 'NSF FEE', 'Overdraft Fee'),
      })),
      loanTransactionsSummary: loanMonths.map((m) => ({
        date: m.date,
        amount: m.total,
        numberOfTransactions: m.count,
        transactions: buildTransactions(m.date, m.amounts, 'LOAN_PAYMENTS', 'Line of Credit Pmt'),
      })),
      createdAt: null,
      updatedAt: null,
    };
  });
};
